import { useUnsyncSmartHold } from "#webapp/src/components/event-card/hooks/useUnsyncSmartHold";
import { CalendarInfoDocument } from "#webapp/src/components/event-card/molecules/__generated__/CalendarInfo.v2.generated";
import { Description } from "#webapp/src/components/event-card/smart-holds/Description";
import { ApolloError } from "@apollo/client";
import { logger } from "@clockwise/client-commons/src/util/logger";
import { Button } from "@clockwise/design-system";
import { Delete } from "@clockwise/icons";
import { SmartHoldState, SmartHoldType } from "@clockwise/schema/v2";
import { useGatewayQuery } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import React from "react";
import toast from "react-hot-toast";
import { usePlannerContext } from "../../Context";
import { Divider } from "../../calendar-popover/atoms/Divider";
import { Footer } from "../Footer";
import { Header } from "../Header";
import { EditSettingsLink } from "./EditSettingsLink";
import { FlexAndProtection } from "./FlexAndProtection";
import { Title } from "./Title";

type SmartHoldForMenu = {
  state: SmartHoldState;
  holdType: SmartHoldType;
};

export const SmartHoldMenu = ({
  smartHold,
  title,
  onOpenCard,
  onClose,
  calendarId,
  externalEventId,
  isOwnSmartHold,
  locked,
}: {
  smartHold: SmartHoldForMenu;
  title: string;
  onClose: () => void;
  onOpenCard: () => void;
  calendarId: string;
  externalEventId: string;
  isOwnSmartHold: boolean;
  locked: boolean;
}) => {
  const { data, loading: calIdLoading } = useGatewayQuery(CalendarInfoDocument, {
    variables: { id: calendarId },
    fetchPolicy: "cache-first",
    skip: isOwnSmartHold, // Skip if you are the user, this is just to get the display name for the title
  });
  const ownerDisplayName = calIdLoading ? "" : data?.calendar?.displayName;
  const firstName = ownerDisplayName?.split(" ")?.[0];
  const titleFormatted = firstName ? title.replace(" ", ` ${firstName}'s `) : title;

  const showFlexibilityStatus = !locked && smartHold.holdType !== SmartHoldType.TravelTime;

  return (
    <>
      <Header
        deleteButton={
          isOwnSmartHold ? (
            <RemoveSmartHold
              calendarId={calendarId}
              externalEventId={externalEventId}
              onClose={onClose}
            />
          ) : null
        }
        onClose={onClose}
        onEdit={onOpenCard}
        canEdit={isOwnSmartHold}
      />
      <div className="cw-px-2">
        <Title title={titleFormatted} onClick={onOpenCard} />
      </div>
      {isOwnSmartHold ? (
        showFlexibilityStatus && (
          <div className="cw-px-2">
            <FlexAndProtection
              onClose={onClose}
              isOwnSmartHold={isOwnSmartHold}
              externalEventId={externalEventId}
              smartHoldState={smartHold.state}
            />
          </div>
        )
      ) : locked ? null : (
        <div className="cw-px-3 cw-pb-2">
          <Divider />
          <div className="cw-pt-1">
            <Description
              type={smartHold.holdType}
              smartHoldState={smartHold.state}
              calendarName={firstName || ""}
            />
          </div>
        </div>
      )}
      {calIdLoading ? null : ownerDisplayName ? (
        <Footer
          loading={calIdLoading}
          canRSVP={false}
          showEventOwner={true}
          ownerDisplayName={ownerDisplayName || ""}
        />
      ) : (
        <div className="cw-px-2">
          <EditSettingsLink />
        </div>
      )}
    </>
  );
};

const RemoveSmartHold = ({
  externalEventId,
  calendarId,
  onClose,
}: {
  externalEventId: string;
  calendarId: string;
  onClose: () => void;
}) => {
  const { refetchEvents } = usePlannerContext();

  const { unsyncSmartHold, loading: unsyncing } = useUnsyncSmartHold(
    () => {
      toast.success("Hold removed");
      onClose();
      void refetchEvents?.();
    },
    (error: ApolloError) => {
      toast.error("Failed to remove smarthold.");
      logger.error("Failed to remove smarthold in popover", error);
    },
  );

  const onUnsyncSmartHold = () => {
    if (!externalEventId) {
      toast.error("Failed to remove smarthold.");
      logger.error("EventId not found, cannot remove smart hold in popover");
      return;
    }
    unsyncSmartHold(externalEventId, calendarId);
  };
  return (
    <Button
      variant="text"
      size="mini"
      startIcon={Delete}
      disabled={unsyncing}
      onClick={onUnsyncSmartHold}
    />
  );
};
